import React, { useEffect, useState } from "react";
import Select from "react-select";
import { PaymentMode } from "../../core/common/selectOption/selectOption";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  Minus,
  PlusCircle,
  ArrowLeft,
} from "feather-icons-react/build/IconComponents";
import { DatePicker } from "antd";
import CustomerServices from "../../services/CustomerServices";
import EmployeeServices from "../../services/EmployeeServices";
import ServiceServices from "../../services/ServiceServices";
import useStore from "../../store/store";
import useAsync from "../../hooks/useAsync";
import { useBilling } from "../../hooks/useBilling";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import { all_routes } from "../../Router/all_routes";

//Nail Services -
const Addsales = () => {
  const route = all_routes;
  const navigate = useNavigate();

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("+91");
  const [isCustomerExists, setIsCustomerExists] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [services, setServices] = useState([
    {
      id: 1,
      service: null,
      quantity: 1,
      price: "",
      assignedEmployee: null,
    },
  ]);

  const selectedStore = useStore((state) => state.selectedStore);
  const storeId = selectedStore?._id;

  // Fetch employees
  const { data: employees } = useAsync(() => {
    const fetchEmployees = async () => {
      if (!storeId) {
        return [];
      }
      const response = await EmployeeServices.getEmployees(storeId);
      return response.map((employee) => ({
        value: employee._id,
        label: employee.name,
      }));
    };
    return fetchEmployees();
  }, [storeId]);

  // Fetch services
  const { data: serviceOptions } = useAsync(() => {
    const fetchServices = async () => {
      if (!storeId) return [];
      const response = await ServiceServices.getServices(storeId);
      return response.map((service) => ({
        value: service._id,
        label: service.name,
        price: service.price,
      }));
    };
    return fetchServices();
  }, [storeId]);

  // State for calculations
  const [totals, setTotals] = useState({
    grossTotal: 0,
    discount: 0,
    cgst: 0,
    sgst: 0,
    netPayable: 0,
  });

  // Calculate totals
  const calculateTotals = (updatedServices) => {
    const grossTotal = updatedServices.reduce(
      (sum, service) => sum + service.price * service.quantity,
      0
    );

    const discount = parseFloat(localStorage.getItem("discount") || 0);
    const cgst = grossTotal * 0.025; // 2.5% CGST on gross total
    const sgst = grossTotal * 0.025; // 2.5% SGST on gross total
    const beforeDiscount = grossTotal + cgst + sgst;
    const netPayable = beforeDiscount - discount;

    const newTotals = {
      grossTotal,
      discount,
      afterDiscount: beforeDiscount, // keeping this for compatibility
      cgst,
      sgst,
      netPayable,
    };

    setTotals(newTotals);
    localStorage.setItem("totals", JSON.stringify(newTotals));
  };

  // Filter out already selected services from options
  const getAvailableServices = (currentIndex) => {
    const selectedServiceIds = services.map(
      (s, idx) => idx !== currentIndex && s.service?.value
    );
    return serviceOptions?.filter(
      (option) => !selectedServiceIds.includes(option.value)
    );
  };

  // Handle service change
  const handleServiceChange = (value, index) => {
    const updatedServices = [...services];
    const selectedService = serviceOptions.find(
      (option) => option.value === value.value
    );

    updatedServices[index] = {
      ...updatedServices[index],
      service: value,
      price: selectedService.price,
      totalPrice: selectedService.price * updatedServices[index].quantity,
    };

    setServices(updatedServices);
    localStorage.setItem("salonServices", JSON.stringify(updatedServices));
    calculateTotals(updatedServices);
  };

  // Handle price change
  const handlePriceChange = (value, index) => {
    const updatedServices = [...services];
    updatedServices[index].price = parseFloat(value) || 0;
    updatedServices[index].totalPrice =
      updatedServices[index].price * updatedServices[index].quantity;
    setServices(updatedServices);
    localStorage.setItem("salonServices", JSON.stringify(updatedServices));
    calculateTotals(updatedServices);
  };

  // Handle quantity change
  const handleQuantityChange = (action, index) => {
    const updatedServices = [...services];
    let newQuantity = updatedServices[index].quantity;

    if (action === "increment") {
      newQuantity += 1;
    } else if (action === "decrement" && newQuantity > 1) {
      newQuantity -= 1;
    }

    updatedServices[index] = {
      ...updatedServices[index],
      quantity: newQuantity,
      totalPrice: updatedServices[index].price * newQuantity,
    };

    setServices(updatedServices);
    localStorage.setItem("salonServices", JSON.stringify(updatedServices));
    calculateTotals(updatedServices);
  };

  // Handle employee assignment
  const handleEmployeeChange = (value, index) => {
    const updatedServices = [...services];
    updatedServices[index].assignedEmployee = value;
    setServices(updatedServices);
  };

  // Add new service row
  const handleAddService = () => {
    setServices([
      ...services,
      {
        id: services.length + 1,
        service: null,
        price: "",
        quantity: 1,
        assignedEmployee: null,
      },
    ]);
  };

  // Remove service row
  const handleRemoveService = (index) => {
    const updatedServices = services.filter((_, i) => i !== index);
    setServices(updatedServices);
    calculateTotals(updatedServices);
  };

  // Handle discount change
  const handleDiscountChange = (value) => {
    const discount = parseFloat(value) || 0;
    localStorage.setItem("discount", discount);
    calculateTotals(services);
  };

  const handleCustomerChange = (e) => {
    setSelectedCustomer(e.target.value);
    setCustomerName(e.target.value);
  };

  const handlePhoneChange = (e) => {
    let input = e.target.value;

    if (!input.startsWith("+91")) {
      input = input.replace(/\D/g, "");
      input = "+91" + input;
    } else {
      input = "+91" + input.substring(3).replace(/\D/g, "");
    }

    if (input.length <= 13) {
      setPhoneNumber(input);
    }
  };

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const customer = await CustomerServices.getCustomerByPhone(
          storeId,
          phoneNumber
        );
        if (customer && customer._id) {
          setSelectedCustomer(customer._id);
          setIsCustomerExists(true);
          // Bind customer name to the textbox if customer exists
          setCustomerName(customer.name);
        } else {
          setSelectedCustomer(null);
          setIsCustomerExists(false);
          // Clear customer name if customer doesn't exist
          setCustomerName("");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setIsCustomerExists(false);
        setCustomerName("");
      }
    };

    if (phoneNumber.length === 13) {
      // +91 + 10 digits
      fetchCustomer();
    } else {
      setSelectedCustomer(null); // Clear selection if phone becomes invalid
      setIsCustomerExists(false);
      setCustomerName("");
    }
  }, [phoneNumber, storeId]);

  useEffect(() => {
    localStorage.removeItem("salonServices");
    localStorage.removeItem("discount");
    localStorage.removeItem("totals");
  }, []);

  const handlePaymentMethodChange = (selected) => {
    setPaymentMethod(selected);
  };

  // Use the billing hook
  const { createBilling } = useBilling();

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate services
    for (let i = 0; i < services.length; i++) {
      const service = services[i];
      if (!service.service?.value) {
        alert(`Please select a service for row ${i + 1}`);
        return;
      }
      if (!service.assignedEmployee?.value) {
        alert(`Please assign an employee for service in row ${i + 1}`);
        return;
      }
      if (!service.price || service.price <= 0) {
        alert(`Please enter a valid price for service in row ${i + 1}`);
        return;
      }
    }

    // Validate payment method
    if (!paymentMethod?.value) {
      alert("Please select a payment method");
      return;
    }

    try {
      const billingData = {
        customer: selectedCustomer,
        date: new Date(),
        services: services.map((service) => ({
          service: service.service?.value,
          quantity: service.quantity,
          price: service.price,
          assignedEmployee: service.assignedEmployee?.value,
          totalPrice: service.quantity * service.price,
        })),
        totals: {
          grossTotal: totals.grossTotal,
          discount: totals.discount,
          cgst: totals.cgst,
          sgst: totals.sgst,
          netPayable: totals.netPayable,
        },
        paymentMethod: paymentMethod?.value,
        status: "pending",
        storeId: storeId,
        isCustomerExists: isCustomerExists,
        customerPhone: phoneNumber, // Include phone number for creating new customer if needed
        customerName: customerName, // Include customer name for creating new customer if needed
      };

      // Use createBilling from useBilling hook instead of direct service call
      // This will automatically update the billings state in the useBilling hook
      let response = await createBilling(billingData);

      // Reset form
      setServices([
        {
          id: 1,
          service: null,
          quantity: 1,
          price: "",
          assignedEmployee: null,
        },
      ]);
      setTotals({
        grossTotal: 0,
        discount: 0,
        cgst: 0,
        sgst: 0,
        netPayable: 0,
      });
      setPaymentMethod(null);
      if (response) {
        localStorage.removeItem("salonServices");
        localStorage.removeItem("discount");
        localStorage.removeItem("totals");
        try {
          await navigate(`/invoice-details/${response?._id}`);
        } catch (navErr) {
          console.error("Navigation error:", navErr);
        }
      }
    } catch (err) {
      alert("Error creating billing:", err);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Add new billing</h4>
              </div>
            </div>
            <ul className="table-top-head">
              <RefreshIcon />
              <CollapesIcon />
              <li>
                <div className="page-btn">
                  <Link to={route.onlineorder} className="btn btn-secondary">
                    <ArrowLeft className="me-2" />
                    Back to Billing
                  </Link>
                </div>
              </li>
            </ul>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="card border-0">
              <div className="card-body pb-0">
                <div className="row">
                  <div className="col-lg-4 col-sm-6 col-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Phone<span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-sm-6 col-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Customer Name
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <div className="row">
                        <div className="col-lg-10 col-sm-10 col-10">
                          <input
                            className="form-control"
                            name="customerName"
                            value={customerName}
                            onChange={handleCustomerChange}
                          />
                          {phoneNumber.length === 13 && (
                            <small
                              className={
                                isCustomerExists
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {isCustomerExists
                                ? "Customer exists"
                                : "New customer"}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4 col-sm-6 col-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Date<span className="text-danger ms-1">*</span>
                      </label>
                      <div className="input-groupicon calender-input">
                        <DatePicker
                          className="form-control datetimepicker"
                          placeholder="dd/mm/yyyy"
                        />
                        <Calendar className="info-img" />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    {services.map((service, index) => (
                      <div
                        key={service.id}
                        className="row align-items-center mb-3"
                      >
                        <div className="col-lg-3 col-sm-6 col-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Service
                              <span className="text-danger ms-1">*</span>
                            </label>
                            <Select
                              classNamePrefix="react-select"
                              options={getAvailableServices(index)}
                              value={service.service}
                              onChange={(value) =>
                                handleServiceChange(value, index)
                              }
                              placeholder="Choose Service"
                            />
                          </div>
                        </div>
                        <div className="col-lg-2 col-sm-6 col-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Qty<span className="text-danger ms-1">*</span>
                            </label>
                            <div className="product-quantity bg-gray-transparent border-0">
                              <span
                                className="quantity-btn"
                                onClick={() =>
                                  handleQuantityChange("decrement", index)
                                }
                              >
                                <Minus />
                              </span>
                              <input
                                type="text"
                                className="form-control bg-transparent"
                                style={{ width: "50px" }}
                                value={service.quantity}
                                readOnly
                              />
                              <span
                                className="quantity-btn"
                                onClick={() =>
                                  handleQuantityChange("increment", index)
                                }
                              >
                                <PlusCircle />
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3 col-sm-6 col-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Price
                              <span className="text-danger ms-1">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={service.price}
                              onChange={(e) =>
                                handlePriceChange(e.target.value, index)
                              }
                              placeholder="Enter price"
                            />
                          </div>
                        </div>
                        <div className="col-lg-3 col-sm-6 col-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Assigned Employee
                              <span className="text-danger ms-1">*</span>
                            </label>
                            <Select
                              classNamePrefix="react-select"
                              options={employees}
                              value={service.assignedEmployee}
                              onChange={(value) =>
                                handleEmployeeChange(value, index)
                              }
                              placeholder="Choose Employee"
                            />
                          </div>
                        </div>
                        <div className="col-lg-1 col-sm-6 col-6 d-flex align-items-end">
                          {services.length > 1 && (
                            <span
                              type="button"
                              onClick={() => handleRemoveService(index)}
                            >
                              <i
                                data-feather="trash-2"
                                className="feather-trash-2"
                              ></i>
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="row">
                      <div className="col-12">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAddService}
                        >
                          <PlusCircle className="me-1" /> Add Service
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-6 ms-auto">
                    <div className="total-order w-100 max-widthauto m-auto mb-4">
                      <ul className="border-1 rounded-2">
                        <li className="border-bottom">
                          <h4 className="border-end">Gross Total</h4>
                          <h5>₹ {totals.grossTotal.toFixed(2)}</h5>
                        </li>
                        <li className="border-bottom">
                          <h4 className="border-end">CGST (2.5%)</h4>
                          <h5>₹ {totals.cgst.toFixed(2)}</h5>
                        </li>
                        <li className="border-bottom">
                          <h4 className="border-end">SGST (2.5%)</h4>
                          <h5>₹ {totals.sgst.toFixed(2)}</h5>
                        </li>
                        <li className="border-bottom">
                          <h4 className="border-end">Discount</h4>
                          <h5>₹ {totals.discount.toFixed(2)}</h5>
                        </li>
                        <li className="border-bottom">
                          <h4 className="border-end">Net Payable</h4>
                          <h5>₹ {totals.netPayable.toFixed(2)}</h5>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-3 col-sm-6 col-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Discount<span className="text-danger ms-1">*</span>
                      </label>
                      <div className="input-groupicon select-code">
                        <input
                          type="text"
                          defaultValue={0}
                          className="form-control p-2"
                          onChange={(e) => handleDiscountChange(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-3 col-sm-6 col-12">
                    <div className="mb-3 mb-5">
                      <label className="form-label">
                        Payment Method
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        options={PaymentMode}
                        placeholder="Choose"
                        name="paymentMethod"
                        onChange={handlePaymentMethodChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Link
                type="button"
                className="btn btn-secondary add-cancel me-3"
                to={route.onlineorder}
              >
                Cancel
              </Link>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  phoneNumber.length === 13 &&
                  !selectedCustomer &&
                  !isCustomerExists
                }
              >
                Submit
              </button>
            </div>
          </form>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0 text-gray-9">
            2014 - 2025 © MissNail. All Right Reserved
          </p>
          <p>
            Designed &amp; Developed by{" "}
            <Link to="#" className="text-primary">
              Dreams
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default Addsales;
